import React, { lazy, Suspense, useContext, useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import Loading from './components/Loading';
import { PrivateRoute } from './components/PrivateRoute';
import NotFound from './pages/NotFound';
import { API, getLogo, getSystemName, isAdmin } from './helpers';
import { UserContext } from './context/User';
import { LanguageProvider, useLanguage } from './helpers/i18n';
import { stringToColor } from './helpers/groupUtils';

const BackendShell = lazy(() => import('./components/BackendShell'));
const Home = lazy(() => import('./pages/Home'));
const Workspace = lazy(() => import('./pages/Workspace'));
const Models = lazy(() => import('./pages/Models'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Docs = lazy(() => import('./pages/Docs'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const About = lazy(() => import('./pages/About'));
const User = lazy(() => import('./pages/User'));
const RegisterForm = lazy(() => import('./components/RegisterForm'));
const LoginForm = lazy(() => import('./components/LoginForm'));
const Setting = lazy(() => import('./pages/Setting'));
const EditUser = lazy(() => import('./pages/User/EditUser'));
const PasswordResetForm = lazy(() => import('./components/PasswordResetForm'));
const GitHubOAuth = lazy(() => import('./components/GitHubOAuth'));
const OidcOAuth = lazy(() => import('./components/OidcOAuth'));
const PasswordResetConfirm = lazy(() => import('./components/PasswordResetConfirm'));
const Channel = lazy(() => import('./pages/Channel'));
const Token = lazy(() => import('./pages/Token'));
const EditChannel = lazy(() => import('./pages/Channel/EditChannel'));
const Redemption = lazy(() => import('./pages/Redemption'));
const TopUp = lazy(() => import('./pages/TopUp'));
const Checkout = lazy(() => import('./pages/Checkout'));
const CheckoutSuccess = lazy(() => import('./pages/Checkout/Success'));
const Log = lazy(() => import('./pages/Log'));
const Midjourney = lazy(() => import('./pages/Midjourney'));
const Detail = lazy(() => import('./pages/Detail'));
const Business = lazy(() => import('./pages/Business'));
const Referral = lazy(() => import('./pages/Referral'));
const Affiliate = lazy(() => import('./pages/Affiliate'));
const AdminOperationSettings = lazy(() => import('./components/AdminOperationSettings'));

function ChannelEditorRoute() {
  const { id } = useParams();
  const navigate = useNavigate();
  if (!isAdmin()) return <Navigate to='/workspace' replace />;
  return <EditChannel key={id ?? 'new'} editingChannel={{ id }} visible handleClose={() => navigate('/channel')} />;
}

const SHOW_AGENTS_NAV = false;
const backendRoutePrefixes = [
  '/workspace',
  '/channel',
  '/token',
  '/business',
  '/admin',
  '/admin/logs',
  '/admin/settings',
  '/redemption',
  '/referral',
  '/user',
  '/setting',
  '/topup',
  '/checkout',
  '/log',
  '/detail',
  '/midjourney'
];

const getStoredUser = () => {
  const storedUser = localStorage.getItem('user');
  if (!storedUser) {
    return null;
  }
  try {
    return JSON.parse(storedUser);
  } catch (error) {
    return null;
  }
};

const getSessionUser = async () => {
  try {
    const response = await fetch('/api/user/self', {
      credentials: 'include',
      headers: {
        Accept: 'application/json'
      }
    });
    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('user');
      }
      return null;
    }
    const payload = await response.json();
    return payload?.success && payload?.data ? payload.data : null;
  } catch (error) {
    return null;
  }
};

function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

function AppContent() {
  const [userState, userDispatch] = useContext(UserContext);
  const [maintenance, setMaintenance] = useState({
    enabled: false,
    notice: ''
  });
  const location = useLocation();
  const isBackendRoute = backendRoutePrefixes.some(prefix => (
    location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
  ));
  const [syncedUser, setSyncedUser] = useState(getStoredUser);
  const currentUser = userState.user || syncedUser || getStoredUser();
  const isRootUser = Boolean(currentUser && Number(currentUser.role) >= 100);
  const canBypassMaintenance = isRootUser || location.pathname === '/login' || location.pathname.startsWith('/oauth/');

  useEffect(() => {
    const storedUser = getStoredUser();
    if (storedUser) {
      setSyncedUser(storedUser);
      userDispatch({ type: 'login', payload: storedUser });
    }
    getSessionUser().then((sessionUser) => {
      if (sessionUser) {
        localStorage.setItem('user', JSON.stringify(sessionUser));
        setSyncedUser(sessionUser);
        userDispatch({ type: 'login', payload: sessionUser });
      }
    });
    API.get('/api/status')
      .then((res) => {
        const { success, data } = res.data;
        if (success && data) {
          setMaintenance({
            enabled: Boolean(data.maintenance_mode),
            notice: data.maintenance_notice || ''
          });
          localStorage.setItem('maintenance_mode', String(Boolean(data.maintenance_mode)));
          localStorage.setItem('maintenance_notice', data.maintenance_notice || '');
        }
      })
      .catch(() => {
        setMaintenance({
          enabled: localStorage.getItem('maintenance_mode') === 'true',
          notice: localStorage.getItem('maintenance_notice') || ''
        });
      });
    let systemName = getSystemName();
    if (systemName) {
      document.title = systemName;
    }
    let logo = getLogo();
    if (logo) {
      let linkElement = document.querySelector('link[rel~=\'icon\']');
      if (linkElement) {
      linkElement.href = logo;
      }
    }
  }, [userDispatch]);

  useEffect(() => {
    setSyncedUser(userState.user || getStoredUser());
  }, [location.pathname, userState.user]);

  if (maintenance.enabled && !canBypassMaintenance) {
    return <MaintenancePage notice={maintenance.notice} />;
  }

  if (isBackendRoute) {
    return (
      <Suspense fallback={<Loading></Loading>}>
        <BackendShell>
          <AppRoutes />
        </BackendShell>
      </Suspense>
    );
  }

  return (
    <PublicShell user={currentUser}>
      <AppRoutes />
    </PublicShell>
  );
}

const AppRoutes = () => (
  <Routes>
    <Route
      path="/"
      element={
        <Suspense fallback={<Loading></Loading>}>
          <Home />
        </Suspense>
      }
    />
    <Route
      path="/models"
      element={
        <Suspense fallback={<Loading></Loading>}>
          <Models />
        </Suspense>
      }
    />
    <Route
      path="/models/:modelId"
      element={
        <Suspense fallback={<Loading></Loading>}>
          <Models />
        </Suspense>
      }
    />
    <Route
      path="/pricing"
      element={
        <Suspense fallback={<Loading></Loading>}>
          <Pricing />
        </Suspense>
      }
    />
    <Route
      path="/docs/*"
      element={
        <Suspense fallback={<Loading></Loading>}>
          <Docs />
        </Suspense>
      }
    />
    <Route
      path="/privacy"
      element={
        <Suspense fallback={<Loading></Loading>}>
          <Privacy />
        </Suspense>
      }
    />
    <Route
      path="/terms"
      element={
        <Suspense fallback={<Loading></Loading>}>
          <Terms />
        </Suspense>
      }
    />
    <Route
      path="/workspace"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <Workspace />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/channel"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <Channel />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/channel/edit/:id"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <ChannelEditorRoute />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/channel/add"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <ChannelEditorRoute />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/token"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <Token />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/business"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <Business />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/admin"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <Business />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/admin/settings"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <AdminOperationSettings />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/redemption"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <Redemption />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/referral"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <Referral />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/affiliate"
      element={
        <Suspense fallback={<Loading></Loading>}>
          <Affiliate />
        </Suspense>
      }
    />
    <Route
      path="/user"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <User />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/user/edit/:id"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <EditUser />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/user/edit"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <EditUser />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/user/reset"
      element={
        <Suspense fallback={<Loading></Loading>}>
          <PasswordResetConfirm />
        </Suspense>
      }
    />
    <Route
      path="/login"
      element={
        <Suspense fallback={<Loading></Loading>}>
          <LoginForm />
        </Suspense>
      }
    />
    <Route
      path="/register"
      element={
        <Suspense fallback={<Loading></Loading>}>
          <RegisterForm />
        </Suspense>
      }
    />
    <Route
      path="/reset"
      element={
        <Suspense fallback={<Loading></Loading>}>
          <PasswordResetForm />
        </Suspense>
      }
    />
    <Route
      path="/oauth/github"
      element={
        <Suspense fallback={<Loading></Loading>}>
          <GitHubOAuth />
        </Suspense>
      }
    />
    <Route
      path="/oauth/oidc"
      element={
        <Suspense fallback={<Loading></Loading>}>
          <OidcOAuth />
        </Suspense>
      }
    />
    <Route
      path="/setting"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <Setting />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/topup"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <TopUp />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/checkout/success"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <CheckoutSuccess />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/checkout"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <Checkout />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/log"
      element={
            <PrivateRoute>
              <Suspense fallback={<Loading></Loading>}>
                <Log mode="usage" />
              </Suspense>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/logs"
          element={
            <PrivateRoute>
              <Suspense fallback={<Loading></Loading>}>
                <Log mode="admin" />
              </Suspense>
            </PrivateRoute>
          }
        />
        <Route
      path="/detail"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <Detail />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/midjourney"
      element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <Midjourney />
          </Suspense>
        </PrivateRoute>
      }
    />
    <Route
      path="/about"
      element={
        <Suspense fallback={<Loading></Loading>}>
          <About />
        </Suspense>
      }
    />
    <Route path="*" element={<NotFound />} />
  </Routes>
);

const MaintenancePage = ({ notice }) => {
  const { t } = useLanguage();
  const logo = getLogo();
  const systemName = getSystemName();
  const message = notice || `${systemName} ${t('nav.support') === '技术支持' ? '正在维护中，请稍后再试。' : 'is currently under maintenance. Please check back soon.'}`;

  return (
    <div className='maintenance-page'>
      <div className='maintenance-card'>
        <img src={logo} alt={`${systemName} logo`} />
        <p className='maintenance-kicker'>{t('nav.support') === '技术支持' ? '维护模式' : 'Maintenance mode'}</p>
        <h1>{systemName}</h1>
        <p>{message}</p>
      </div>
    </div>
  );
};

const PublicShell = ({ children, user }) => {
  const { language, setLanguage, t } = useLanguage();
  const logo = getLogo();
  const systemName = getSystemName();
  const currentUser = user || getStoredUser();
  const loggedIn = Boolean(currentUser);
  const location = useLocation();
  const isAuthRoute = ['/login', '/register', '/reset'].some(path => location.pathname === path);

  return (
    <div className='public-shell'>
      {isAuthRoute ? null : (
        <header className='public-nav'>
          <Link className='public-brand' to='/'>
            <img src={logo} alt='GPTokens logo' />
            <span>{systemName}</span>
          </Link>
          <nav className='public-nav-links'>
            <Link to='/models'>{t('nav.models')}</Link>
            {SHOW_AGENTS_NAV ? <Link to='/agents'>{t('nav.agents')}</Link> : null}
            <a href='/pricing'>{t('nav.price')}</a>
            <Link to='/affiliate'>{t('nav.affiliate')}</Link>
            <Link to='/docs'>{t('nav.docs')}</Link>
            <Link to={loggedIn ? '/workspace' : '/login'}>{t('nav.workspace')}</Link>
          </nav>
          <div className='public-nav-actions'>
            <div className='language-switch' aria-label='Language switch'>
              <button
                className={language === 'en' ? 'active' : ''}
                type='button'
                onClick={() => setLanguage('en')}
              >
                EN
              </button>
              <button
                className={language === 'zh' ? 'active' : ''}
                type='button'
                onClick={() => setLanguage('zh')}
              >
                中文
              </button>
            </div>
            {loggedIn ? (
              <Link className='public-user-button' to='/workspace' aria-label='Open workspace'>
                <span
                  className='public-user-avatar'
                  style={{ backgroundColor: stringToColor(currentUser?.username || currentUser?.display_name || systemName) }}
                >
                  {(currentUser?.display_name || currentUser?.username || 'U').slice(0, 1).toLowerCase()}
                </span>
                <span>{currentUser?.display_name || currentUser?.username || t('nav.workspace')}</span>
              </Link>
            ) : (
              <Link className='public-login-button' to='/login'>
                {t('nav.login')}
              </Link>
            )}
          </div>
        </header>
      )}
      <main className={`public-main ${isAuthRoute ? 'auth-public-main' : ''}`}>{children}</main>
      {isAuthRoute ? null : (
        <footer className='public-footer'>
          <span>© 2026 GPTokens</span>
          <div>
            <Link to='/privacy'>{t('nav.privacy')}</Link>
            <Link to='/terms'>{t('nav.terms')}</Link>
            <Link to='/docs'>{t('nav.support')}</Link>
            <a href='/tools/claude-ban-risk'>{language === 'zh' ? 'Claude 封号风险监测' : 'Claude network risk check'}</a>
          </div>
        </footer>
      )}
    </div>
  );
};

export default App;
